import { Link, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  FolderKanban,
  ScanSearch,
  FileBarChart,
  Settings,
  Menu,
  X,
  LineChart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import IndexMark from './IndexMark';
import { useIsAdmin } from '@/hooks/useIsAdmin';

export type IndexNavKey = 'home' | 'mandates' | 'audit' | 'reports' | 'settings' | 'admin' | 'reviewer';

const NAV_ITEMS: { key: IndexNavKey; label: string; path: string; icon: typeof LayoutGrid; adminOnly?: boolean }[] = [
  { key: 'home', label: 'Index Home', path: '/home', icon: LayoutGrid },
  { key: 'mandates', label: 'Development Mandates', path: '/home', icon: FolderKanban },
  { key: 'audit', label: 'Nuclius Audit', path: '/workflow', icon: ScanSearch },
  { key: 'reports', label: 'Nuclius Index Reports', path: '/scorecards', icon: FileBarChart },
  { key: 'settings', label: 'Settings', path: '/preferences', icon: Settings },
  { key: 'reviewer', label: 'Script reviewer', path: '/reviewer', icon: ScanSearch, adminOnly: true },
  { key: 'admin', label: 'Instrumentation', path: '/admin/instrumentation', icon: LineChart, adminOnly: true },
];

type IndexSidebarProps = {
  activeNav?: IndexNavKey;
  mobileOpen: boolean;
  onMobileClose: () => void;
};

export default function IndexSidebar({ activeNav, mobileOpen, onMobileClose }: IndexSidebarProps) {
  const location = useLocation();
  const { isAdmin } = useIsAdmin();

  const isActive = (item: (typeof NAV_ITEMS)[0]) => {
    if (activeNav) return item.key === activeNav;
    if (item.key === 'audit') return location.pathname.startsWith('/workflow');
    if (item.key === 'reports')
      return location.pathname.startsWith('/scorecards') || location.pathname.startsWith('/analysis');
    if (item.key === 'settings') return location.pathname.startsWith('/preferences');
    if (item.key === 'reviewer') return location.pathname.startsWith('/reviewer');
    if (item.key === 'admin') return location.pathname.startsWith('/admin/instrumentation');
    return location.pathname === '/home' && (item.key === 'home' || item.key === 'mandates');
  };

  const visibleItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const nav = (
    <nav className="flex flex-col gap-0.5 py-4">
      <p className="ni-label px-4 mb-3">Workspace</p>
      {visibleItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item);
        return (
          <Link
            key={item.key}
            to={item.path}
            onClick={onMobileClose}
            className={cn(
              'flex items-center gap-3 px-4 py-2.5 text-sm font-sans transition-colors border-l-2 border-transparent',
              active ? 'ni-nav-active' : 'text-white/55 hover:text-white hover:bg-white/[0.03]',
            )}
          >
            <Icon className="h-4 w-4 shrink-0 opacity-70" />
            {item.label}
          </Link>
        );
      })}
      <div className="mt-8 px-4 space-y-2">
        <p className="ni-label">Shortcuts</p>
        <p className="text-[10px] font-mono text-white/30">Audit Run · F9</p>
        <p className="text-[10px] font-mono text-white/30">Export · F12</p>
      </div>
    </nav>
  );

  return (
    <>
      <aside className="hidden lg:flex w-56 shrink-0 flex-col border-r border-[#2a2a2a] bg-[#0a0a0a] min-h-screen">
        <div className="px-4 py-5 border-b border-[#2a2a2a]">
          <IndexMark />
        </div>
        {nav}
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <button
            type="button"
            className="absolute inset-0 bg-black/70"
            aria-label="Close menu"
            onClick={onMobileClose}
          />
          <aside className="relative w-64 bg-[#0a0a0a] border-r border-[#2a2a2a] min-h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-4 border-b border-[#2a2a2a]">
              <IndexMark />
              <button type="button" onClick={onMobileClose} className="text-white/60 p-1">
                <X className="h-5 w-5" />
              </button>
            </div>
            {nav}
          </aside>
        </div>
      )}
    </>
  );
}

export function IndexMobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="lg:hidden p-2 text-white/60 hover:text-white"
      aria-label="Open menu"
    >
      <Menu className="h-5 w-5" />
    </button>
  );
}
